import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { X, Upload, Camera, Search, Download } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Webcam from 'react-webcam';
import 'react-lazy-load-image-component/src/effects/blur.css';
import CinemaFooter from '../components/gallery/cinema/CinemaFooter';
import { getVisitorId, trackVisit } from '../lib/visitorTracking';

// match-confidence pill — Best (green) / Good (teal) / Possible (amber)
const confidencePill = (score) => {
  if (!score && score !== 0) return null;
  let bg = '#FEF3C7', fg = '#92400E', label = 'Possible';
  if (score >= 85) { bg = '#DCFCE7'; fg = '#15803D'; label = 'Best match'; }
  else if (score >= 70) { bg = '#CFFAFE'; fg = '#0E7490'; label = 'Good'; }
  return (
    <div className="font-tiq-mono" style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', background: bg, color: fg, padding: '3px 7px', borderRadius: 5 }}>
      {label} · {Math.round(score)}%
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const FindMeGallery = () => {
  const { findMeToken } = useParams();
  const [gallery, setGallery] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [showEmailGate, setShowEmailGate] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [searchingFaces, setSearchingFaces] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);

  useEffect(() => {
    loadGalleryInfo();
    trackVisit(`${BACKEND_URL}/api/findme/${findMeToken}/track`);
  }, [findMeToken]);

  const loadGalleryInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/findme/${findMeToken}`);
      setGallery(response.data);
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast.error('Gallery not found');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast.error('Please enter both your name and email');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/findme/${findMeToken}/verify-email`,
        { email, name, visitor_id: getVisitorId() }
      );
      
      setSessionToken(response.data.session_token);
      setShowEmailGate(false);
      toast.success(`Welcome ${name}! Upload a photo to find yourself.`);
    } catch (error) {
      console.error('Error verifying email:', error);
      toast.error('Failed to verify email');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    await searchFaces(file);
  };

  const handleCaptureSelfie = async () => {
    if (!webcamRef.current) return;

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      toast.error('Failed to capture image');
      return;
    }

    // Convert base64 to File
    const response = await fetch(imageSrc);
    const blob = await response.blob();
    const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });

    setShowWebcam(false);
    await searchFaces(file);
  };

  const searchFaces = async (file) => {
    setSearchingFaces(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_token', sessionToken);

      const response = await axios.post(
        `${BACKEND_URL}/api/findme/${findMeToken}/face-search?session_token=${sessionToken}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const matchedPhotos = response.data.matches || [];
      
      if (matchedPhotos.length === 0) {
        toast.info('No matching faces found in this gallery');
        setPhotos([]);
      } else {
        setPhotos(matchedPhotos);
        toast.success(`Found ${matchedPhotos.length} photo${matchedPhotos.length !== 1 ? 's' : ''} with you!`);
      }
    } catch (error) {
      console.error('Error searching faces:', error);
      toast.error(error.response?.data?.detail || 'Failed to search for faces');
      setPhotos([]);
    } finally {
      setSearchingFaces(false);
    }
  };

  const downloadPhoto = async (photo, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    try {
      const url = `${BACKEND_URL}/api/findme/${findMeToken}/photo/${photo.id}/download?session_token=${sessionToken}`;
      
      const response = await fetch(url);
      const blob = await response.blob();
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = photo.filename || `photo-${photo.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download photo');
    }
  };

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tiq-nearblack flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 rounded-full animate-spin mb-4" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }}></div>
          <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-tiq-nearblack flex items-center justify-center text-white">
        <div className="text-center px-6">
          <h1 className="font-tiq-display uppercase mb-2" style={{ fontWeight: 900, fontSize: 32, letterSpacing: '-0.02em' }}>Gallery not found</h1>
          <p style={{ color: 'rgba(255,255,255,0.55)' }}>This gallery does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // ---------- Email Gate ----------
  if (showEmailGate) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-tiq-body relative" style={{ background: 'radial-gradient(120% 120% at 50% 0%, #16161A 0%, #0A0A0B 70%)' }}>
        <Toaster position="top-center" richColors />
        <img src="/turbo-mark-white.png" alt="" className="absolute pointer-events-none" style={{ top: '-40px', right: '-40px', width: 'min(40vw,380px)', opacity: 0.06, transform: 'rotate(-8deg)' }} />
        <div className="w-full max-w-md relative" style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
          <div className="p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 14, background: '#BF212F' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" /><path d="M21 21l-3.5-3.5" /><circle cx="11" cy="9.5" r="2" /><path d="M7.5 15c0.6-1.6 2-2.4 3.5-2.4s2.9 0.8 3.5 2.4" />
                </svg>
              </div>
            </div>
            <p className="tiq-kicker text-center" style={{ fontSize: 11, color: '#8A8884' }}>Find your photos</p>
            <h1 className="font-tiq-display text-center uppercase mt-1" style={{ color: '#141414', fontWeight: 800, fontSize: 26, letterSpacing: '-0.02em', lineHeight: 1.05 }}>{gallery.name}</h1>
            <p className="text-center text-sm mt-3" style={{ color: '#55555C' }}>Enter your details, then take a selfie — we'll find every shot you're in.</p>

            <form onSubmit={handleEmailSubmit} className="space-y-4 mt-6">
              <div>
                <label className="font-tiq-heading block text-sm font-semibold mb-2" style={{ color: '#141414' }}>Your name</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" required
                  className="w-full px-4 py-3 rounded-lg focus:outline-none"
                  style={{ border: '2px solid #E2DED6' }}
                  onFocus={(e) => (e.target.style.borderColor = '#BF212F')}
                  onBlur={(e) => (e.target.style.borderColor = '#E2DED6')}
                />
              </div>
              <div>
                <label className="font-tiq-heading block text-sm font-semibold mb-2" style={{ color: '#141414' }}>Your email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" required
                  className="w-full px-4 py-3 rounded-lg focus:outline-none"
                  style={{ border: '2px solid #E2DED6' }}
                  onFocus={(e) => (e.target.style.borderColor = '#BF212F')}
                  onBlur={(e) => (e.target.style.borderColor = '#E2DED6')}
                />
              </div>
              <button type="submit" className="w-full font-tiq-heading text-white font-semibold py-3 transition-all" style={{ background: '#BF212F', borderRadius: 10 }}>
                Continue to selfie →
              </button>
              <p className="text-center text-xs" style={{ color: '#8A8884' }}>
                Your details are used only to deliver your photos. We never share them.
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Main Find-Me screen ----------
  const matchCount = photos.length;
  return (
    <div className="min-h-screen bg-tiq-nearblack font-tiq-body">
      <Toaster position="top-center" richColors />

      {/* Header — red → dark-red gradient */}
      <header className="relative overflow-hidden text-white text-center" style={{ background: 'linear-gradient(135deg,#BF212F,#8B1923)', padding: 'clamp(40px,6vw,72px) clamp(16px,3vw,38px) clamp(34px,5vw,56px)' }}>
        <img src="/turbo-mark-white.png" alt="" className="absolute pointer-events-none" style={{ top: '-40px', right: '-40px', width: 'min(36vw,360px)', opacity: 0.12, transform: 'rotate(-8deg)' }} />
        <div className="relative">
          <img src="/turbo-logo-white.png" alt="Turbo 360" className="mx-auto" style={{ height: 26, width: 'auto', marginBottom: 22 }} />
          <p className="tiq-kicker" style={{ fontSize: 'clamp(11px,1.4vw,13px)', color: 'rgba(255,255,255,0.85)' }}>{gallery.name}</p>
          <h1 className="font-tiq-display uppercase m-0 mt-3" style={{ fontWeight: 900, fontSize: 'clamp(34px,6vw,68px)', lineHeight: 0.92, letterSpacing: '-0.025em' }}>Your Photos</h1>
          <div className="inline-flex items-center gap-2 mt-5" style={{ background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '8px 16px' }}>
            <span className="tiq-ai-pill font-tiq-mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 7px', borderRadius: 5 }}>AI</span>
            <span className="font-tiq-heading" style={{ fontWeight: 600, fontSize: 14 }}>
              {matchCount > 0 ? `${matchCount} photo${matchCount !== 1 ? 's' : ''} found` : 'Upload a photo to search'}
            </span>
          </div>
        </div>
      </header>

      <main style={{ background: '#0A0A0B', padding: 'clamp(28px,4vw,48px) clamp(16px,3vw,38px) 0' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          {/* Instructions + Upload */}
          <div style={{ background: '#141416', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 'clamp(20px,3vw,32px)', marginBottom: 28 }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-7">
              {[
                ['1', 'Take a selfie', 'Or upload a clear photo of yourself'],
                ['2', 'AI face search', "We'll scan every photo in the gallery"],
                ['3', 'Download', 'Save every shot you appear in'],
              ].map(([n, t, d]) => (
                <div key={n} className="flex items-start gap-3">
                  <div className="flex-shrink-0 flex items-center justify-center font-tiq-mono" style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(191,33,47,0.18)', color: '#BF212F', fontWeight: 700 }}>{n}</div>
                  <div>
                    <h3 className="font-tiq-heading font-semibold text-white" style={{ fontSize: 14 }}>{t}</h3>
                    <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{d}</p>
                  </div>
                </div>
              ))}
            </div>

            {!showWebcam ? (
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  onClick={() => setShowWebcam(true)} disabled={searchingFaces}
                  className="font-tiq-heading inline-flex items-center gap-2 text-white"
                  style={{ background: '#BF212F', borderRadius: 10, padding: '14px 22px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
                >
                  <Camera className="w-5 h-5" /> Take a selfie
                </button>
                <label className="cursor-pointer">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} disabled={searchingFaces} className="hidden" />
                  <div className="font-tiq-heading inline-flex items-center gap-2 text-white" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 10, padding: '14px 22px', fontWeight: 600, fontSize: 15 }}>
                    {searchingFaces ? (
                      <><div className="w-5 h-5 rounded-full animate-spin" style={{ border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff' }}></div> Searching…</>
                    ) : (
                      <><Upload className="w-5 h-5" /> Upload a photo</>
                    )}
                  </div>
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative rounded-xl overflow-hidden mx-auto" style={{ border: '2px solid #BF212F', maxWidth: 460, background: '#000' }}>
                  <Webcam ref={webcamRef} audio={false} screenshotFormat="image/jpeg" className="w-full h-auto" videoConstraints={{ width: 1280, height: 720, facingMode: 'user' }} />
                </div>
                <div className="flex gap-3 justify-center">
                  <button onClick={handleCaptureSelfie} className="font-tiq-heading inline-flex items-center gap-2 text-white" style={{ background: '#BF212F', borderRadius: 10, padding: '12px 20px', fontWeight: 600, cursor: 'pointer' }}>
                    <Camera className="w-5 h-5" /> Capture &amp; search
                  </button>
                  <button onClick={() => setShowWebcam(false)} className="font-tiq-heading text-white" style={{ border: '1px solid rgba(255,255,255,0.22)', borderRadius: 10, padding: '12px 20px', fontWeight: 600, background: 'transparent', cursor: 'pointer' }}>
                    ← Back
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          {photos.length > 0 && (
            <div style={{ paddingBottom: 'clamp(40px,6vw,64px)' }}>
              <div className="tiq-uniform-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                {photos.map((photo, index) => (
                  <div key={photo.id} className="group relative overflow-hidden cursor-pointer tiq-fade" style={{ aspectRatio: '4 / 5', background: '#1a1a1c' }}>
                    {confidencePill(photo.confidence ?? photo.similarity)}
                    <div onClick={() => openLightbox(index)} className="w-full h-full">
                      <LazyLoadImage
                        src={photo.thumbnail_url || photo.preview_url}
                        alt={`Photo ${index + 1}`}
                        effect="blur"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        wrapperClassName="w-full h-full"
                      />
                    </div>
                    <div className="absolute inset-0 flex items-end justify-end opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.72))', padding: 10 }}>
                      <button onClick={(e) => downloadPhoto(photo, e)} title="Download" style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: '#BF212F', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <Download className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No results */}
          {photos.length === 0 && !searchingFaces && sessionToken && (
            <div className="text-center" style={{ padding: 'clamp(40px,6vw,72px) 20px clamp(48px,7vw,80px)' }}>
              <div className="mx-auto mb-5 flex items-center justify-center" style={{ width: 72, height: 72, borderRadius: '50%', border: '2px dashed rgba(255,255,255,0.25)' }}>
                <Search className="w-7 h-7" style={{ color: 'rgba(255,255,255,0.4)' }} />
              </div>
              <h3 className="font-tiq-display uppercase text-white" style={{ fontWeight: 800, fontSize: 22, letterSpacing: '-0.015em' }}>No matches yet</h3>
              <p className="mt-2" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>Take a selfie or upload a clear photo to find yourself in the gallery.</p>
            </div>
          )}
        </div>
        <CinemaFooter />
      </main>

      {/* Lightbox */}
      {lightboxOpen && photos[currentPhotoIndex] && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(0,0,0,0.95)' }}>
          <div className="flex items-center justify-between" style={{ padding: '16px clamp(16px,3vw,28px)' }}>
            <div className="font-tiq-mono text-white" style={{ fontSize: 12, letterSpacing: '0.1em' }}>
              {String(currentPhotoIndex + 1).padStart(2, '0')} / {String(photos.length).padStart(2, '0')}
            </div>
            <button onClick={closeLightbox} style={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center min-h-0" style={{ gap: 'clamp(8px,2vw,16px)', padding: '0 clamp(8px,2vw,18px)' }}>
            {photos.length > 1 && (
              <button onClick={prevPhoto} style={{ width: 46, height: 46, flexShrink: 0, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer', fontSize: 22 }}>‹</button>
            )}
            <img src={photos[currentPhotoIndex].preview_url || photos[currentPhotoIndex].original_url} alt="" className="object-contain" style={{ maxHeight: '100%', maxWidth: '82%' }} />
            {photos.length > 1 && (
              <button onClick={nextPhoto} style={{ width: 46, height: 46, flexShrink: 0, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer', fontSize: 22 }}>›</button>
            )}
          </div>
          <div className="flex justify-center" style={{ padding: '16px clamp(16px,3vw,28px) clamp(20px,3vw,28px)' }}>
            <button onClick={(e) => downloadPhoto(photos[currentPhotoIndex], e)} className="font-tiq-heading inline-flex items-center gap-2 text-white" style={{ background: '#BF212F', borderRadius: 8, padding: '11px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              <Download className="w-4 h-4" /> Download
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FindMeGallery;
